import {Checkbox, Drawer, FormControlLabel, Stack, Switch, TextField, Typography} from "@mui/material";
import React, {useEffect, useState} from "react";
import type {ClosedOnDays} from "../../firebase/index.js";
import {addPlace, convertAnchoredDateLocaleTextToMillis, generateStringKeyFromMillis, PlaceModel, TripModel} from "../../firebase/index.js";
import {useInMemoryTrip} from "../../redux/InMemoryTripReducer.js";
import APXActionButtons from "../../reusables/APXActionButtons.jsx";
import APXCheckboxDependentOptionSelector from "../../reusables/APXCheckboxDependentOptionSelector.jsx";
import type {BSLProps} from "../../reusables/js/APXBottomSheetLayoutUtils.js";
import useBottomSheet from "../../reusables/js/APXBottomSheetLayoutUtils.js";
import {eatery, shop} from "../../reusables/js/APXTheme.js";
import DaysButtonGroup, {DAYS_OF_WEEK} from "./DaySelector.jsx";


export default function AddNewPlaceBSL({uid, onClose, place}: BSLProps & { place: PlaceModel }) {
	  const [placeName, setPlaceName] = useState("");
	  const [isEatery, setIsEatery] = useState(false);
	  const [isVeg, setIsVeg] = useState(false);
	  const [isShop, setIsShop] = useState(false);
	  const [isPhotographyAllowed, setIsPhotographyAllowed] = useState(true);
	  const [closedOn, setClosedOn] = useState([]);
	  const [anchoredDateLocaleText, setAnchoredDateLocaleText] = useState(null);
	  const [note, setNote] = useState("");
	  
	  const [isChecked, setIsChecked] = useState(false);
	  
	  const [placeKey, setPlaceKey] = useState("");
	  useEffect(() => {
			 const p: PlaceModel = place;
			 if (/*TRUTHY->*/ !!p) {
					setPlaceKey(p.placeKey);
					setPlaceName(p.name);
					setIsEatery(p.isEatery);
					setIsVeg(p.isVeg);
					setIsShop(p.isShop);
					setIsPhotographyAllowed(p.isPhotographyAllowed);
					setClosedOn(Object.entries(p.closedOnDays).filter(([DDD, isClosed]) => isClosed).map(([DDD, isClosed]) => DDD));
					setAnchoredDateLocaleText(p.anchoredOnDate && new Date(p.anchoredOnDate).toLocaleDateString());
					setNote(p.note);
			 }
	  }, [place]);
	  
	  const inMemoryTrip: TripModel = useInMemoryTrip();
	  
	  const {isOpen, handleOpen, handleClose} = useBottomSheet({initialState: true, onClose});
	  
	  const handleCancel = () => {
			 onClose();
	  };
	  
	  const handleAdd = () => {
			 if (/*FALSY->*/ !!!uid) {
					alert("Please login to add a trip");
					return;
			 }
			 if (placeName.trim() === "") {
					alert("Place name cannot be empty");
					return;
			 }
			 if (Object.keys(inMemoryTrip.tripDates).length > 0 && isChecked && anchoredDateLocaleText === null) {
					alert("Anchored date cannot be empty");
					return;
			 }
			 if (/*TRUTHY->*/ !!anchoredDateLocaleText) {
					const DDD = new Date(convertAnchoredDateLocaleTextToMillis(anchoredDateLocaleText)).toLocaleDateString("en-GB", {weekday: "short"}).toUpperCase();
					if (closedOn.includes(DDD)) {
						  alert("Place is closed on anchored date. Please select another date.");
						  return;
					}
			 }
			 
			 const key = placeKey || generateStringKeyFromMillis();
			 
			 const anchoredOnDate = convertAnchoredDateLocaleTextToMillis(anchoredDateLocaleText);
			 
			 const closedOnDays: ClosedOnDays = {};
			 DAYS_OF_WEEK.forEach((day) => {
					closedOnDays[day] = closedOn.includes(day);
			 });
			 
			 const place = PlaceModel.asPlace(key, placeName.trim(), note.trim(), isEatery, isVeg, isShop, isPhotographyAllowed, closedOnDays, anchoredOnDate);
			 
			 addPlace(uid, inMemoryTrip.itineraryKey, anchoredOnDate, key, place);
			 
			 handleCancel();
	  };
	  
	  useEffect(() => {
			 if (/*NOT->*/ !isEatery) {
					setIsVeg(false);
			 }
			 if (isEatery) {
					setIsShop(false);
			 }
	  }, [isEatery]);
	  
	  useEffect(() => {
			 if (isShop) {
					setIsEatery(false);
			 }
	  }, [isShop]);
	  
	  return (
				 <Drawer anchor="bottom" open={isOpen} onClose={handleClose} variant="temporary">
						<Stack spacing={2} p={2}>
							  <TextField
										 required
										 fullWidth
										 variant="outlined"
										 label="Place"
										 value={placeName}
										 onChange={(e) => setPlaceName(e.target.value)}
							  />
							  <Stack direction="row" alignItems="center" justifyContent="center">
									 
									 <Checkbox sx={{color: eatery, "&.Mui-checked": {color: eatery}}}
												  checked={isEatery} onChange={(event) => setIsEatery(event.target.checked)}/>
									 <Typography sx={{color: eatery}}>Eatery</Typography>
									 
									 <Checkbox sx={{color: eatery, "&.Mui-checked": {color: eatery}, ml: 1}} disabled={/*NOT->*/ !isEatery}
												  checked={isVeg} onChange={(event) => setIsVeg(event.target.checked)}/>
									 <Typography sx={{color: eatery}}>Veg</Typography>
									 
									 <Checkbox sx={{color: shop, "&.Mui-checked": {color: shop}, ml: 1}}
												  checked={isShop} onChange={(event) => setIsShop(event.target.checked)}/>
									 <Typography sx={{color: shop}}>Shop</Typography>
							  
							  </Stack>
							  
							  <Stack direction="row" alignItems="center" justifyContent="center">
									 <FormControlLabel control={
											<Switch color="primary" checked={isPhotographyAllowed} onChange={(e) => setIsPhotographyAllowed(e.target.checked)}/>
									 } label="Photography allowed" labelPlacement="start"/>
							  </Stack>
							  
							  <Stack justifyContent="center" alignItems={"center"}>
									 <DaysButtonGroup valueArray={closedOn} header={"Closed on"} onChange={(days: string[]) => setClosedOn(days)}/>
							  </Stack>
							  {
									 /*FALSY->*/ !!!place ?
										 <APXCheckboxDependentOptionSelector
													disabled={Object.keys(inMemoryTrip.tripDates).length === 0}
													checkboxLabel={"Anchor"}
													options={Object.values(inMemoryTrip.tripDates)}
													textFieldLabel={"to a specific date"}
													value={anchoredDateLocaleText}
													onOptionSelected={(newValue) => {
														  setAnchoredDateLocaleText(newValue);
													}}
													checked={isChecked}
													onCheckedChange={(checked: boolean) => setIsChecked(checked)}
										 />
										 :
										 anchoredDateLocaleText && <Typography align={"center"} sx={{mt: 2}}>Anchored on: {anchoredDateLocaleText}</Typography>
							  }
							  <TextField
										 fullWidth
										 variant="outlined"
										 label="Note"
										 multiline
										 rows={1}
										 value={note}
										 onChange={(e) => setNote(e.target.value)}
							  />
							  <APXActionButtons onCancel={handleCancel} onAdd={handleAdd}/>
						</Stack>
				 </Drawer>
	  );
}